import type { FC, HTMLAttributes } from "react";
import { useTranslation } from "react-i18next";
import { Link, matchPath, useLocation } from "react-router";
import labproLogo from "@/assets/labpro.svg";
import { LabTestStatus, PROCESSING_STATUSES } from "@/models";
import { routes } from "@/routes";
import { useLayout } from "@/state/LayoutContext";
import { type BreadcrumbItem, Breadcrumbs } from "./Breadcrumbs";
import { UserInfo } from "./UserInfo";

export const AppTopBar: FC<HTMLAttributes<HTMLDivElement>> = (props) => {
  const { t } = useTranslation("components");
  const location = useLocation();
  const { pageTitle, activeTestStatus } = useLayout();

  const isProcessing = activeTestStatus ? PROCESSING_STATUSES.has(activeTestStatus) : false;
  const testsListHref = isProcessing
    ? `${routes.processingTests.root}?status=${activeTestStatus}`
    : activeTestStatus && activeTestStatus !== LabTestStatus.Completed
      ? `${routes.tests.root}?status=${activeTestStatus}`
      : routes.tests.root;

  const isAgs = location.pathname.startsWith(routes.ags.root);
  const isReports = location.pathname.startsWith(routes.reports.root);
  const breadcrumbs: BreadcrumbItem[] = [];

  if (isAgs) {
    breadcrumbs.push({ label: t("appTopBar.agsExport", "AGS export"), href: routes.ags.root });
  } else if (isReports) {
    const isCreatingReport = matchPath(routes.reports.create, location.pathname) !== null;
    breadcrumbs.push({ label: t("appTopBar.reports", "Reports"), href: routes.reports.root });
    if (pageTitle) {
      breadcrumbs.push({ label: pageTitle, href: isCreatingReport ? routes.reports.root : undefined });
    }
    if (isCreatingReport) {
      breadcrumbs.push({ label: t("appTopBar.reportsCreate", "Create report") });
    }
  } else if (!matchPath(routes.accessManagement, location.pathname)) {
    breadcrumbs.push({ label: t("appTopBar.tests"), href: testsListHref });
  }

  if (matchPath(routes.ags.project, location.pathname)) {
    breadcrumbs.push({
      label: pageTitle || t("appTopBar.details"),
      href: location.pathname,
    });
  } else if (matchPath(routes.tests.new, location.pathname)) {
    breadcrumbs.push({
      label: t("appTopBar.newTests"),
      href: location.pathname,
    });
  } else if (
    matchPath(routes.tests.detail, location.pathname) ||
    matchPath(routes.processingTests.detail, location.pathname)
  ) {
    breadcrumbs.push({
      label: pageTitle || t("appTopBar.details"),
      href: location.pathname,
    });
  }

  return (
    <div
      {...props}
      className="flex flex-[1_0_auto] w-full min-w-0 h-app-bar-height px-layout-xlarge items-center justify-between gap-layout-medium rounded-none bg-popover shadow-raised"
    >
      <div className="flex-[1_1_auto] min-w-0 flex">
        <div className="flex items-center gap-layout-xsmall min-w-0">
          <Link to={routes.home} className="flex items-center shrink-0">
            <img
              src={labproLogo}
              alt={t("appTopBar.logoAlt", { product: "LabPRO" })}
              className="block h-[28px] brightness-0"
            />
          </Link>
          <Breadcrumbs items={breadcrumbs} />
        </div>
      </div>

      <div className="flex-none flex justify-end items-center">
        <UserInfo />
      </div>
    </div>
  );
};
